import { getCurrencySuggestions } from '@/api/generated/codebooks/codebooks';
import type { CurrencySuggestion, ListRequest } from '@/api/generated/schemas';
import { createAsyncSelectorField } from '@/shared/forms';
import {
  defineEntityAsyncSelector,
  listRequestQueryKey,
  selectorPresets,
  toListRequest,
  type NoPickFilters,
} from '@/shared/ui/async-selector';

/** UI ref měny — identitou je legacy `Currency.ID`. */
export interface CurrencyRef {
  id: number;
  /** ISO kód, např. `CZK`. */
  code: string;
  /**
   * Lidský zápis měny, např. `Kč` (legacy `Currency.Vyjadreni`). Není to plný název měny —
   * sloupec `Nazev` Magic neplní, takže se nemapuje a selektor ho nenabízí.
   */
  name: string;
}

/**
 * Obecný selektor měn nad číselníkem `dbo.Currency` — nepatří žádné agendě, konzumenti ho
 * importují z veřejné plochy modulu Codebooks. Popiskem je ISO kód, sekundárně lidský zápis.
 * Handle (ne komponenta) je zároveň to, co si bere `filter.selector` v ListPage.
 */
export const currencySelector = defineEntityAsyncSelector<
  CurrencyRef,
  CurrencySuggestion,
  ListRequest,
  NoPickFilters
>({
  api: {
    fetch: getCurrencySuggestions,
    buildApiParams: (ctx) => toListRequest(ctx.search, ctx.ids),
    queryKey: (request) => listRequestQueryKey('codebooks.currencies', request),
    mapItem: (dto) => ({ id: dto.id, code: dto.code ?? '', name: dto.name ?? '' }),
  },
  key: (item) => item.id,
  display: {
    kind: 'nameCode',
    name: (item) => item.code,
    code: (item) => item.name,
  },
  columns: (t) =>
    selectorPresets.nameAndCode({
      name: {
        id: 'code',
        header: t('currencySelector.columns.code'),
        value: (item) => item.code,
        sortable: true,
      },
      code: {
        id: 'name',
        header: t('currencySelector.columns.name'),
        value: (item) => item.name,
        emptyDash: '—',
      },
    }),
  i18n: { namespace: 'codebooks' },
});

export const CurrencySelector = currencySelector.Selector;
export const CurrencySelectorField = createAsyncSelectorField(CurrencySelector);
